export const HIDDEN_PROPERTY_NAME = "_original_variant_id";
export const CART_SOURCE = {
  KEY_NAME: "_source",
  SUITS_OPTION: "HYDROGEN",
  CORE_OPTION: "THEME",
  SHARED_OPTION: "SHARED",
  CART_KEY: "Cart Type",
};
export const RUSH_FEE_KEY = "_rush_fee_amount";

/**
 * withoutEdgesAndNodes function goes through Storefront API data and removes unnecesssary lines, edges and nodes, so we can get organized unnested data.
 * @data {*} Array or Object
 * @returns Array or Object
 */

export const withoutEdgesAndNodes = (data) => {
  function isArray(entry) {
    return entry && entry.constructor === Array;
  }

  function isObject(entry) {
    return entry && entry.constructor === Object;
  }
  let result = Array.isArray(data) ? [] : {};

  if (!isObject(data) && !isArray(data)) return data;

  Object.keys(data).forEach((key) => {
    if (typeof key === "string" && key === "edges") {
      result = withoutEdgesAndNodes(data.edges.map((edge) => edge.node));
    } else {
      result = Object.assign(result, {
        [key]: withoutEdgesAndNodes(data[key]),
      });
    }
  });

  return result;
};

/**
 * parseGid function takes the global Shopify Storefront IDs and filters out the 'gid://' tag to grab the ID number categorized into specific groups (e.g. ProductVariant/123456 => 123456). If the ID is already in the correct format, return the original ID
 * @gid {*} String — global Shopify ID
 * @returns Number
 */

export const parseGid = (gid) => {
  if (gid.startsWith("gid://shopify/" || gid !== Number)) {
    const match = gid.match(/gid:\/\/shopify\/[^/]+\/([a-f0-9-]+)/);
    if (match) return match[1];
  }
  return gid;
};

/**
 * This function is used to convert Storefront API cart line attributes into the Ajax cart line properties format:
 * e.g. [{ key: _original_variant_id, value: 12345 }] => [{_original_variant_id: 12345}]
 * @param {*} arr
 * @returns
 */
export const convertPropertiesToAjaxFormat = (arr) =>
  arr.reduce((acc, item) => {
    acc[item.key] = item.value;
    return acc;
  }, {});

/**
 * This function is used to convert Ajax cart line properties into Storefront API cart line attributes format. This is later sent to Storefront API to add/update cart line items.
 * e.g. [{_original_variant_id: 12345}] => [{ key: _original_variant_id, value: 12345 }]
 * @param {*} arr
 * @returns
 */
export const convertAttributesFromAjaxToStorefront = (arr) => {
  const convertedArr = arr.map((item) => {
    if (item.key && item.value) {
      return { key: item.key, value: item.value };
    }
    const key = Object.keys(item)[0];
    const value = item[key];
    return { key, value };
  });

  // Add '_source' as a cart line item attribute to track origin.
  convertedArr.push({
    key: CART_SOURCE.KEY_NAME,
    value: CART_SOURCE.CORE_OPTION,
  });

  return convertedArr;
};

/**
 * converStorefrontToAjaxFormat function takes the Storefront API data and mutates the information to match the Ajax Cart API data format, so we can use the data in non-modularized JS functions in `scripts.js`, `swatch-discount.js`...etc.
 * @storefrontData {*} Object
 * @returns Object
 */
export const convertStorefrontToAjaxFormat = (storefrontData) => {
  const {
    attributes,
    checkoutUrl,
    cost,
    discountCodes,
    id,
    lines,
    totalQuantity,
  } = storefrontData;

  // Helper function for formatting price
  const formatPrice = (price) => Math.floor(Number(price?.amount)) * 100;

  // Helper function for getting the product handle from URL
  const getProductHandle = (url) => url.split("/products/")[1];

  // Helper function for formatting discount
  const getDiscountAmount = (allocations) => {
    const { discountedAmount } = allocations[0] || [];
    return formatPrice(discountedAmount) || null;
  };

  // Helper function to format individual items
  const formatItem = (item) => {
    const {
      attributes: itemAttributes,
      id: itemId,
      merchandise,
      quantity,
      discountAllocations,
    } = item;

    const {
      title,
      compareAtPrice,
      price,
      product,
      selectedOptions,
      sku,
      id: productId,
    } = merchandise;

    const totalDiscount = getDiscountAmount(discountAllocations);
    const formattedPrice = formatPrice(price);
    const formattedComparePrice = formatPrice(compareAtPrice) || null;
    const vendor = product?.vendor?.toLowerCase();
    const productHandle = vendor?.includes("birdy grey suits")
      ? handleize(product?.title)
      : getProductHandle(product?.onlineStoreUrl);
    const updatedAttributes = convertPropertiesToAjaxFormat(itemAttributes);

    return {
      key: itemId,
      id: merchandise.id,
      quantity,
      properties: updatedAttributes,
      title,
      tags: product.tags,
      vendor: product.vendor,
      product_title: product.title,
      variant_title: title,
      variant_options: [selectedOptions.map((opt) => opt.value)],
      variant_id: merchandise.id,
      price: formattedPrice,
      original_price: formattedPrice,
      discounted_price: formattedComparePrice,
      line_price: formattedComparePrice,
      final_price: formattedComparePrice,
      final_line_price: formattedPrice * quantity - totalDiscount,
      original_line_price: formattedPrice * quantity,
      total_discount: totalDiscount,
      sku,
      product_id: productId,
      product_type: product.productType,
      gift_card: product.isGiftCard,
      url: `/products/${productHandle}`,
      image: product?.featuredImage?.url,
      featured_image: {
        height: product?.featuredImage?.height,
        width: product?.featuredImage?.width,
        altText: product?.featuredImage?.altText,
      },
      options_with_values: selectedOptions,
      handle: productHandle,
    };
  };

  const updatedItems = lines ? withoutEdgesAndNodes(lines).map(formatItem) : [];
  return {
    checkoutUrl,
    attributes,
    currency: cost?.totalAmount.currencyCode,
    item_count: totalQuantity,
    items: updatedItems,
    items_subtotal_price: formatPrice(cost.subtotalAmount),
    original_total_price: formatPrice(cost.totalAmount),
    requires_shipping: true,
    token: id,
    total_discount: discountCodes,
    total_price: formatPrice(cost.totalAmount),
  };
};

/**
 * Helper function to set cookie `cart-token` into document. Used in `createCartToken`
 * @param {*} cname
 * @param {*} cvalue
 * @param {*} exdays
 */
export const setCookie = (cname, cvalue, exdays = 30) => {
  const d = new Date();
  d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
  const expires = `expires=${d.toUTCString()}`;
  const domain = `Domain=${getRootDomain()};`;
  document.cookie = `${cname}=${cvalue};${expires};path=/;${domain}`;
};
/**
 * Helper function to grab the `cart-token` cookie. Used in `fetchStorefrontCart` and `handleAjaxCart`
 * @param {*} cname
 * @returns
 */
export const getCookie = (cname) => {
  const name = `${cname}=`;
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return undefined;
};

window.getCookie = getCookie;
/**
 * This is a helper function for `handleAjaxCart` function in `cart--api.js`, where we check to see if there is an AJAX cart present after the Storefront Cart feature flag is toggled on to grab that cart's information, convert it and sent a POST request to add the AJAX items into the new cart. We need to format the information to match the addToCart mutation variable requirements.
 * @param {*} ajaxData
 * @returns
 */
export const convertAjaxCartAndAdd = (ajaxData) => {
  const formatProperties = (properties) =>
    Object.entries(properties || {}).map(([key, value]) => ({
      key,
      value:
        key === HIDDEN_PROPERTY_NAME
          ? `gid://shopify/ProductVariant/${value}`
          : String(value),
    }));

  return ajaxData.map((item) => ({
    merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`,
    quantity: item.quantity,
    attributes: item.properties
      ? [
          ...formatProperties(item.properties),
          { key: "_ajax_line_key", value: item.key },
        ]
      : null,
  }));
};

const { KEY_NAME, SUITS_OPTION, CORE_OPTION, SHARED_OPTION } = CART_SOURCE;

export const checkItemSources = (items) => {
  let coreItems = 0;
  let suitItems = 0;

  for (const item of items) {
    const source = item.properties[KEY_NAME];

    if (source === SUITS_OPTION) {
      suitItems++;
    } else if (source === CORE_OPTION) {
      coreItems++;
    }
  }

  if (coreItems === items.length) {
    return CORE_OPTION;
  }
  if (suitItems === items.length) {
    return SUITS_OPTION;
  }
  return SHARED_OPTION;
};
